/**
 * What to call a model, in one place.
 *
 * The version the model gave for itself wins; our catalogue label is the
 * fallback. "GPT-5.6 Luna" rather than "ChatGPT — GPT-5", and a real name
 * rather than "Other model" for anything off the list — which every
 * unrecognised model is filed under, so that bucket only grows.
 *
 * The label is no longer independent evidence of anything. Members used to
 * pick their model from a dropdown; now the answer names itself and the stored
 * label is derived from that same self-report (see matchModelFromSelfReport on
 * the server), so showing the label instead shows a coarsened copy of the value
 * this returns.
 *
 * Here rather than beside any one screen because the rule kept being applied on
 * some surfaces and not others: the comparison table and the Blogger posts were
 * fixed first, and the research lists and the save confirmation went on naming
 * the catalogue entry for another round. One definition, so the next screen to
 * show a model name gets it right by importing rather than by remembering.
 *
 * System forecasts are unaffected. They carry no self-report at all, so this
 * returns their label exactly as before.
 */

/** The positional form, for callers holding the two strings rather than a row. */
export const displayModelName = (label, modelId, claimed) => {
  const name = String(claimed || '').trim();
  return name || String(label || '').trim();
};

/**
 * The row-shaped form. Accepts either naming convention, because the API
 * serves snake_case and the components that pass their own objects around use
 * camelCase.
 */
export const modelNameOf = (row) => {
  if (!row) return '';
  const claimed = row.model_self_report ?? row.modelSelfReport;
  const label = row.model_label ?? row.modelLabel;
  // Last resort for a row that has neither: the stored model id, which is
  // prefixed on member research and is at least a name rather than a blank.
  const fallback = String(row.model || '').replace(/^user:/, '');
  return displayModelName(label || fallback, null, claimed);
};
